import { useMemo } from "react";
import { useContacts } from "../../context/ContactsContext";

export const useContactHeader = () => {
  const {
    contactDetails: contactDetailsFromState,
    selectedContact,
    phones,
    emails,
    loadingContacts,
    loadingDetails,
    loadingPhones,
    loadingEmails,
  } = useContacts();

  const baseContact = contactDetailsFromState || selectedContact || null;
  const loading =
    loadingContacts || loadingDetails || loadingPhones || loadingEmails;

  const { contactDetails, hasContact } = useMemo(() => {
    if (!baseContact) {
      return {
        contactDetails: null,
        hasContact: false,
      };
    }

    // ---- PHONES ----
    const activePhones = (phones || []).filter((p) => !p.markedDeleted);
    const primaryPhone =
      activePhones.find((p) => p.isPrimary) || activePhones[0] || null;

    const additionalPhonesCount = Math.max(
      activePhones.length - (primaryPhone ? 1 : 0),
      0
    );

    // ---- EMAILS ----
    const activeEmails = (emails || []).filter((e) => !e.markedDeleted);
    const primaryEmail =
      activeEmails.find((e) => e.isPrimary) || activeEmails[0] || null;

    const additionalEmailsCount = Math.max(
      activeEmails.length - (primaryEmail ? 1 : 0),
      0
    );

    // ---- NAME ----
    const name =
      baseContact.legalName ||
      [baseContact.firstName, baseContact.lastName]
        .filter(Boolean)
        .join(" ") ||
      "Unnamed contact";

    // ---- ADDRESS / POSTAL / COMPANY ----
    // Build address string from object fields, or use flat addressLine if available
    const addressFromObject = baseContact.address
      ? [
          baseContact.address.addressLine,
          baseContact.address.addressCity,
          baseContact.address.addressState,
        ].filter(Boolean).join(', ')
      : null;

    const address =
      baseContact.addressLine ||
      addressFromObject ||
      null;

    const postalCode =
      baseContact.addressPostalCode ||
      baseContact.address?.addressPostalCode ||
      baseContact.postalCode ||
      "N/A";

    const company = baseContact.company || "N/A";

    const mergedContactDetails = {
      name,
      phone:
        primaryPhone?.phoneNumber ||
        baseContact.phone ||
        "No phone available",
      email:
        primaryEmail?.emailAddress ||
        baseContact.email ||
        "No email available",
      additionalPhonesCount,
      additionalEmailsCount,
      address,
      postalCode,
      company,
    };

    return {
      contactDetails: mergedContactDetails,
      hasContact: true,
    };
  }, [baseContact, phones, emails]);

  return {
    contactDetails,
    loading,
    hasContact,
  };
};
